import { Icons, Progress } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import { useNotification } from 'hooks/useNotification'
import { ListRequestParams } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { AButton } from 'views/components/Analytics'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from 'views/pages/LogisticsKR/analyticsPlaces'
import { CarStatuses } from '../../../types'
import { CarEditModal } from '../../CarEditModal/CarEditModal'

const ButtonWrapper = styled.div`
  display: flex;
  column-gap: 8px;
`

type ActionsCellProps = {
  data: any
  setListRequestParams: (ListRequestParams) => void
  listRequestParams: ListRequestParams
}
export const ActionsCell: FC<ActionsCellProps> = ({ data, setListRequestParams, listRequestParams }) => {
  const { t } = useTranslation('logisticsKR')
  const notify = useNotification()
  const { ready_for_active, status, id } = data

  const [editModal, setEditModal] = useState(false)
  const [archivedConfirmModal, setArchivedConfirmModal] = useState(false)
  const [changeStatusConfirmModal, setChangeStatusConfirmModal] = useState(false)

  const handleChangeStatusToArchived = async (carId: string) => {
    await apiClient.post(endpoints.LogisticsKR.carStatus(carId), { new_status: CarStatuses.archived })
    setArchivedConfirmModal(false)
    setListRequestParams({ ...listRequestParams })
  }

  const handleChangeStatusToActive = async (carId: string) => {
    await apiClient.post(endpoints.LogisticsKR.carStatus(carId), { new_status: CarStatuses.active })
    setChangeStatusConfirmModal(false)
    setListRequestParams({ ...listRequestParams })
  }

  const [changeStatusToArchivedProgress, changeStatusToArchived] = useAProgress(handleChangeStatusToArchived, {
    eventName: 'setStatusToArchived',
    onSuccess: () => notify(Progress.SUCCESS, { title: t('carsEdit.notifications.car_archived_success') }),
    onError: () => notify(Progress.ERROR, { title: t('carsEdit.notifications.car_archived_error') }),
  })
  const [changeStatusToActiveProgress, changeStatusToActive] = useAProgress(handleChangeStatusToActive, {
    eventName: 'setStatusToActive',
    onSuccess: () => notify(Progress.SUCCESS, { title: t('carsEdit.notifications.car_active_success') }),
    onError: () => notify(Progress.ERROR, { title: t('carsEdit.notifications.car_active_error') }),
  })

  return (
    <ButtonWrapper>
      {status === CarStatuses.draft && (
        <AButton
          id="changeStatus"
          color="secondary"
          size="small"
          Icon={Icons.IconCheck}
          onClick={() => setChangeStatusConfirmModal(true)}
        />
      )}
      {status !== CarStatuses.archived && (
        <AButton id="edit" color="secondary" size="small" Icon={Icons.IconEdit} onClick={() => setEditModal(true)} />
      )}
      {status !== CarStatuses.archived && (
        <AButton
          id="archive"
          analyticsPayload={{ id }}
          color="destruct"
          progress={changeStatusToArchivedProgress}
          size="small"
          Icon={Icons.IconDelete}
          onClick={() => setArchivedConfirmModal(true)}
        />
      )}
      {archivedConfirmModal && (
        <ASharedModal
          id={AnalyticsPlaces.CARS.ARCHIVE_MODAL}
          size="small"
          onClose={() => setArchivedConfirmModal(false)}
        >
          <SharedModalTitle>{t('carsDetails.archiveForm.title')}</SharedModalTitle>
          <SharedModalContent>{t('carsDetails.archiveForm.text')}</SharedModalContent>
          <ADefaultModalFooter
            progress={changeStatusToArchivedProgress}
            onClose={() => setArchivedConfirmModal(false)}
            onConfirm={() => changeStatusToArchived(id)}
            confirmButtonText={t('common:archive')}
          />
        </ASharedModal>
      )}
      {editModal && (
        <CarEditModal
          car={data}
          onClose={() => setEditModal(false)}
          setListRequestParams={setListRequestParams}
          listRequestParams={listRequestParams}
        />
      )}
      {changeStatusConfirmModal && (
        <ASharedModal
          id={AnalyticsPlaces.CARS.CHANGE_STATUS_MODAL}
          size="small"
          onClose={() => setChangeStatusConfirmModal(false)}
        >
          <SharedModalTitle>{t('cars.confirmModals.statusChangingTitle')}</SharedModalTitle>
          <SharedModalContent>
            {ready_for_active
              ? t('cars.confirmModals.youReallyWantToChangeStatus')
              : t('cars.confirmModals.allFieldsMustBeFilledIn')}
          </SharedModalContent>
          <ADefaultModalFooter
            progress={changeStatusToActiveProgress}
            disabled={!ready_for_active}
            onConfirm={() => changeStatusToActive(id)}
            onClose={() => setChangeStatusConfirmModal(false)}
          />
        </ASharedModal>
      )}
    </ButtonWrapper>
  )
}
